/**
 * AgentInboxTab Component
 * AIDLC Administrative Backend
 *
 * Displays queued prompts for an agent in an email inbox style.
 * Shows subject, from, priority, status, and preview of prompt content.
 */

import { useState, useEffect, useCallback } from 'react';
import { getAgentInbox } from '../../services/agentAssetService';
import { LoadingSpinner } from '../shared/LoadingSpinner';
import { Badge } from '../shared/Badge';
import type { PromptQueueItem, PromptStatus, PromptPriority } from '../../types/chat';

interface Props {
  agentId: string;
  agentName: string;
}

const STATUS_BADGE: Record<PromptStatus, { variant: 'warning' | 'info' | 'success' | 'danger'; label: string }> = {
  pending: { variant: 'warning', label: 'Pending' },
  processing: { variant: 'info', label: 'Processing' },
  completed: { variant: 'success', label: 'Completed' },
  failed: { variant: 'danger', label: 'Failed' },
};

const PRIORITY_STYLES: Record<PromptPriority, { color: string; label: string }> = {
  urgent: { color: 'text-red-600', label: '!!!' },
  high: { color: 'text-orange-500', label: '!!' },
  normal: { color: 'text-gray-400', label: '!' },
  low: { color: 'text-gray-300', label: '-' },
};

type FilterStatus = 'all' | PromptStatus;

export function AgentInboxTab({ agentId, agentName }: Props) {
  const [prompts, setPrompts] = useState<PromptQueueItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState<FilterStatus>('all');
  const [selectedId, setSelectedId] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await getAgentInbox(agentId);
      setPrompts(data);
    } catch {
      // non-critical
    } finally {
      setLoading(false);
    }
  }, [agentId]);

  useEffect(() => { load(); }, [load]);

  if (loading) return <LoadingSpinner message="Loading inbox..." size="sm" />;

  const filtered = filter === 'all' ? prompts : prompts.filter(p => p.status === filter);
  const selected = selectedId ? prompts.find(p => p.id === selectedId) : null;

  const pendingCount = prompts.filter(p => p.status === 'pending').length;
  const processingCount = prompts.filter(p => p.status === 'processing').length;

  return (
    <div className="space-y-4">
      {/* Inbox header */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-sm font-semibold text-ldaja-black flex items-center gap-2">
            <svg className="w-4 h-4 text-gray-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
            </svg>
            Inbox
            {pendingCount > 0 && (
              <span className="px-1.5 py-0.5 bg-ldaja-blue text-white text-[10px] rounded-full font-bold">
                {pendingCount}
              </span>
            )}
          </h3>
          <p className="text-xs text-gray-400 mt-0.5">
            Queued prompts for {agentName}
          </p>
        </div>
        <button
          onClick={load}
          className="text-xs text-gray-500 hover:text-ldaja-blue transition-colors flex items-center gap-1"
        >
          <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
          </svg>
          Refresh
        </button>
      </div>

      {/* Filter tabs */}
      <div className="flex gap-1 border-b border-gray-200 pb-0">
        {([
          { id: 'all' as FilterStatus, label: 'All', count: prompts.length },
          { id: 'pending' as FilterStatus, label: 'Pending', count: pendingCount },
          { id: 'processing' as FilterStatus, label: 'In Progress', count: processingCount },
          { id: 'completed' as FilterStatus, label: 'Completed', count: prompts.filter(p => p.status === 'completed').length },
        ]).map(tab => (
          <button
            key={tab.id}
            onClick={() => setFilter(tab.id)}
            className={`px-3 py-2 text-xs font-medium border-b-2 transition-colors ${
              filter === tab.id
                ? 'border-ldaja-blue text-ldaja-blue'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            {tab.label}
            {tab.count > 0 && (
              <span className="ml-1 text-[10px] text-gray-400">({tab.count})</span>
            )}
          </button>
        ))}
      </div>

      {filtered.length === 0 ? (
        <div className="text-center py-12">
          <svg className="w-12 h-12 mx-auto text-gray-300 mb-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1}>
            <path d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
          </svg>
          <p className="text-sm text-gray-500">
            {filter === 'all' ? 'No prompts in inbox' : `No ${filter} prompts`}
          </p>
          <p className="text-xs text-gray-400 mt-1">
            Prompts queued for {agentName} will appear here.
          </p>
        </div>
      ) : (
        <div className="flex gap-0 border border-gray-200 rounded-lg overflow-hidden" style={{ minHeight: 320 }}>
          {/* Message list (left side) */}
          <div className={`${selected ? 'w-2/5' : 'w-full'} border-r border-gray-200 overflow-y-auto divide-y divide-gray-100`}>
            {filtered.map(prompt => {
              const isSelected = selectedId === prompt.id;
              const isPending = prompt.status === 'pending';
              const priorityStyle = PRIORITY_STYLES[prompt.priority] || PRIORITY_STYLES.normal;

              return (
                <button
                  key={prompt.id}
                  onClick={() => setSelectedId(isSelected ? null : prompt.id)}
                  className={`w-full text-left px-4 py-3 transition-colors ${
                    isSelected ? 'bg-ldaja-blue/5 border-l-2 border-ldaja-blue' : 'hover:bg-gray-50 border-l-2 border-transparent'
                  }`}
                >
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-1.5">
                        <span className={`text-xs font-bold ${priorityStyle.color}`}>{priorityStyle.label}</span>
                        <span className={`text-sm truncate ${isPending ? 'font-semibold text-ldaja-black' : 'font-normal text-gray-700'}`}>
                          {prompt.subject}
                        </span>
                      </div>
                      <div className="flex items-center gap-2 mt-0.5">
                        <span className="text-[10px] text-gray-400">from: {prompt.from}</span>
                        <Badge variant={STATUS_BADGE[prompt.status].variant}>
                          {STATUS_BADGE[prompt.status].label}
                        </Badge>
                      </div>
                      <p className="text-[11px] text-gray-400 truncate mt-0.5">{prompt.prompt}</p>
                    </div>
                    <span className="text-[10px] text-gray-400 whitespace-nowrap flex-shrink-0">
                      {formatInboxDate(prompt.createdAt)}
                    </span>
                  </div>
                </button>
              );
            })}
          </div>

          {/* Reading pane (right side) */}
          {selected && (
            <div className="w-3/5 overflow-y-auto">
              <div className="px-6 py-4 border-b border-gray-200 bg-gray-50/50">
                <h4 className="text-base font-semibold text-ldaja-black">{selected.subject}</h4>
                <div className="flex items-center gap-3 mt-1.5 text-xs text-gray-500">
                  <span>From: <span className="font-medium text-gray-700">{selected.from}</span></span>
                  <span>Priority: <span className={`font-medium ${PRIORITY_STYLES[selected.priority].color}`}>{selected.priority}</span></span>
                  <Badge variant={STATUS_BADGE[selected.status].variant}>
                    {STATUS_BADGE[selected.status].label}
                  </Badge>
                </div>
                <div className="flex items-center gap-4 mt-1 text-[10px] text-gray-400">
                  <span>Queued: {new Date(selected.createdAt).toLocaleString()}</span>
                  {selected.processedAt && <span>Started: {new Date(selected.processedAt).toLocaleString()}</span>}
                  {selected.completedAt && <span>Completed: {new Date(selected.completedAt).toLocaleString()}</span>}
                </div>
              </div>

              {/* Prompt content */}
              <div className="px-6 py-4">
                {selected.personaContext && (
                  <div className="mb-3 text-xs">
                    <span className="font-semibold text-gray-600">Persona Context:</span>
                    <p className="text-gray-500 mt-0.5">{selected.personaContext}</p>
                  </div>
                )}
                {selected.projectContext && (
                  <div className="mb-3 text-xs">
                    <span className="font-semibold text-gray-600">Project Context:</span>
                    <p className="text-gray-500 mt-0.5">{selected.projectContext}</p>
                  </div>
                )}
                <div className="mb-3">
                  <span className="text-xs font-semibold text-gray-600">Prompt:</span>
                  <div className="mt-1 p-3 bg-gray-50 rounded-lg border border-gray-200">
                    <pre className="text-xs text-gray-700 whitespace-pre-wrap font-mono">{selected.prompt}</pre>
                  </div>
                </div>
                {selected.result && (
                  <div className="mt-4">
                    <span className="text-xs font-semibold text-gray-600">Result:</span>
                    <div className="mt-1 p-3 bg-green-50 rounded-lg border border-green-200">
                      <pre className="text-xs text-gray-700 whitespace-pre-wrap font-mono">{selected.result}</pre>
                    </div>
                  </div>
                )}
                {selected.tokensUsed && (
                  <div className="mt-3 text-[10px] text-gray-400">
                    Tokens used: {selected.tokensUsed.toLocaleString()}
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function formatInboxDate(dateStr: string): string {
  const date = new Date(dateStr);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

  if (diffDays === 0) {
    return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  }
  if (diffDays === 1) return 'Yesterday';
  if (diffDays < 7) return date.toLocaleDateString([], { weekday: 'short' });
  return date.toLocaleDateString([], { month: 'short', day: 'numeric' });
}
